/** Tableau de bord - KPIs, présences par département, pointages, bornes */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AlertTriangle, FileDown, FileWarning, LogIn, LogOut } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { dashboardApi, timeLogsApi } from '../services/api';
import { parseServerDate } from '../lib/utils';
import { useIncompleteLogs } from '../hooks/useTimeLogs';

const API_URL = import.meta.env.VITE_API_URL;

// --- Donut SVG (sans dépendance) ---

function Donut({ rate, color = '#E06D43' }: { rate: number; color?: string }) {
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - rate);
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" className="-rotate-90">
      <circle cx="36" cy="36" r={radius} fill="none" stroke="#F4F2EE" strokeWidth="8" />
      <circle
        cx="36"
        cy="36"
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="transition-all duration-500"
      />
    </svg>
  );
}

// --- KPI Card ---

function KpiCard({
  label,
  value,
  sub,
  tone = 'default',
  accent,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: 'default' | 'alert' | 'dark';
  accent?: React.ReactNode;
}) {
  if (tone === 'dark') {
    return (
      <div className="bg-sidebar text-white rounded-xl p-5 shadow-soft relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/20 to-transparent" />
        <div className="relative">
          <p className="text-[11px] uppercase tracking-widest text-stone-400 font-medium">{label}</p>
          <p className="num text-3xl font-bold mt-2">{value}</p>
          {sub && <p className="text-xs text-stone-400 mt-3">{sub}</p>}
        </div>
      </div>
    );
  }
  if (tone === 'alert') {
    return (
      <div className="bg-accent-soft rounded-xl p-5 border border-accent/30">
        <p className="text-[11px] uppercase tracking-widest text-accent font-semibold">{label}</p>
        <p className="num text-3xl font-bold mt-2 text-accent">{value}</p>
        {sub && <p className="text-xs text-stone-600 mt-3">{sub}</p>}
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl p-5 border border-stone-200/80 shadow-soft">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-stone-500 font-medium">{label}</p>
          <p className="num text-3xl font-bold mt-2 text-ink">{value}</p>
        </div>
        {accent}
      </div>
      {sub && <p className="text-xs text-stone-500 mt-3">{sub}</p>}
    </div>
  );
}

// --- Rapport du jour : download PDF ---

async function downloadDailyReport(site_id?: number) {
  const token = localStorage.getItem('token');
  const params = new URLSearchParams();
  if (site_id) params.set('site_id', String(site_id));
  const today = format(new Date(), 'yyyy-MM-dd');
  params.set('date', today);
  const res = await fetch(`${API_URL}/api/reports/daily?${params}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    alert('Erreur lors de la génération du rapport');
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `rapport-pointbreak-${today}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

// --- Page ---

export function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardApi.stats(),
    refetchInterval: 30_000,
  });

  const { data: presence, isLoading: presenceLoading } = useQuery({
    queryKey: ['dashboard-presence'],
    queryFn: () => dashboardApi.presence(),
    refetchInterval: 30_000,
  });

  const { data: byDept } = useQuery({
    queryKey: ['dashboard-by-department'],
    queryFn: () => dashboardApi.byDepartment(),
    refetchInterval: 30_000,
  });

  const { data: recentLogs } = useQuery({
    queryKey: ['dashboard-recent-logs'],
    queryFn: () => timeLogsApi.list({ per_page: 8, page: 1 }),
    refetchInterval: 30_000,
  });

  const { data: incomplete } = useIncompleteLogs(7);

  const unjustifiedAbsent = useMemo(
    () => presence?.absent_today.filter((e) => e.was_scheduled && !e.has_justified_absence) ?? [],
    [presence?.absent_today]
  );

  const justifiedAbsent = useMemo(
    () => presence?.absent_today.filter((e) => e.has_justified_absence) ?? [],
    [presence?.absent_today]
  );

  const total = stats?.today.total_employees ?? 0;
  const present = stats?.today.present_count ?? 0;
  const absent = stats?.today.absent_count ?? 0;
  const presenceRate = total > 0 ? present / total : 0;

  const today = new Date();

  return (
    <div>
      {/* Header personnalisé */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-stone-500 font-medium">
            {format(today, 'EEEE d MMMM yyyy', { locale: fr })}
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-ink tracking-tight mt-1">
            Tableau de bord
          </h2>
          <p className="text-sm text-stone-600 mt-1">
            Activité temps réel · {total} employés · {stats?.readers_status.length ?? 0} bornes
          </p>
        </div>
        <Button variant="dark" onClick={() => downloadDailyReport()}>
          <FileDown size={14} className="mr-2" /> Rapport du jour
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          label="Présents"
          value={
            <>
              {present}
              <span className="text-sm text-stone-400 font-normal">/{total}</span>
            </>
          }
          accent={
            <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
              {(presenceRate * 100).toFixed(0)}%
            </span>
          }
          sub={`Taux de présence actuel`}
        />
        <KpiCard
          label="Absents"
          value={absent}
          sub={`${justifiedAbsent.length} justifiés · ${unjustifiedAbsent.length} non justifiés`}
        />
        <KpiCard
          label="⚠ À traiter"
          value={unjustifiedAbsent.length}
          sub="Absences non justifiées"
          tone={unjustifiedAbsent.length > 0 ? 'alert' : 'default'}
        />
        <KpiCard label="Total employés" value={total} sub={`${stats?.readers_status.length ?? 0} bornes actives`} tone="dark" />
      </div>

      {/* Présence par département */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card
          title="Taux de présence par département"
          className="lg:col-span-2"
          action={<span className="text-[11px] text-stone-500">Mis à jour à l'instant</span>}
        >
          {!byDept?.departments.length ? (
            <p className="text-sm text-stone-400">Aucun département</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {byDept.departments.map((d) => {
                const color =
                  d.rate >= 0.9 ? '#059669' : d.rate >= 0.7 ? '#E06D43' : '#DC2626';
                return (
                  <div
                    key={`${d.name}-${d.site_name}`}
                    className="flex items-center gap-4 p-3 rounded-lg bg-stone-50 border border-stone-200/60"
                  >
                    <div className="relative flex-shrink-0">
                      <Donut rate={d.rate} color={color} />
                      <span className="absolute inset-0 flex items-center justify-center text-sm font-bold num text-ink">
                        {(d.rate * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-ink truncate">{d.name}</p>
                      <p className="text-[11px] text-stone-500">{d.site_name}</p>
                      <p className="text-xs num text-stone-600 mt-1">
                        <span className="font-semibold text-ink">{d.present}</span>
                        <span className="text-stone-400">/{d.total}</span> présents
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card title="État des bornes">
          {statsLoading ? (
            <p className="text-sm text-stone-400">Chargement...</p>
          ) : !stats?.readers_status.length ? (
            <p className="text-sm text-stone-400">Aucune borne configurée</p>
          ) : (
            <div className="space-y-2.5">
              {stats.readers_status.map((reader) => (
                <div
                  key={reader.code}
                  className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-stone-50"
                >
                  <div
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      reader.status === 'online'
                        ? 'bg-emerald-500 ring-4 ring-emerald-100'
                        : 'bg-stone-300'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink truncate">{reader.name}</p>
                    <p className="text-[11px] text-stone-500">
                      {reader.site_name} ·{' '}
                      {reader.status === 'online' ? 'En ligne' : 'Hors ligne'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Derniers pointages */}
      <Card
        title="Derniers pointages"
        action={
          <Link to="/time-logs" className="text-xs text-accent hover:underline font-medium">
            Tout voir →
          </Link>
        }
        className="mb-6"
        noPadding
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-stone-500 border-b border-stone-200">
                <th className="px-5 py-2 font-medium">Employé</th>
                <th className="py-2 font-medium">Type</th>
                <th className="py-2 font-medium">Heure</th>
                <th className="py-2 font-medium">Borne</th>
                <th className="py-2 px-5 font-medium">Site</th>
              </tr>
            </thead>
            <tbody>
              {!recentLogs?.items.length ? (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-stone-400 text-sm">
                    Aucun pointage récent
                  </td>
                </tr>
              ) : (
                recentLogs.items.map((log, i) => (
                  <tr
                    key={log.id}
                    className={`border-b border-stone-100 last:border-0 ${
                      i % 2 === 1 ? 'bg-stone-50/50' : ''
                    }`}
                  >
                    <td className="px-5 py-2.5 font-medium text-ink">
                      {log.employee.first_name} {log.employee.last_name}
                    </td>
                    <td className="py-2.5">
                      {log.type === 'IN' ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                          <LogIn size={10} /> ENTRÉE
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-stone-600 bg-stone-100 px-2 py-0.5 rounded">
                          <LogOut size={10} /> SORTIE
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 num text-stone-700">
                      {format(parseServerDate(log.timestamp), 'HH:mm')}
                    </td>
                    <td className="py-2.5 text-stone-600">{log.reader.name}</td>
                    <td className="py-2.5 px-5 text-stone-500">{log.reader.code.split('_')[0]}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pointages incomplets */}
      {incomplete && incomplete.length > 0 && (
        <Card
          title={`Pointages incomplets — ${incomplete.length}`}
          className="mb-6 border-amber-200"
          action={
            <Link to="/time-logs" className="text-xs text-accent hover:underline font-medium">
              Corriger →
            </Link>
          }
        >
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            {incomplete.map((day) => (
              <div
                key={`${day.employee_id}-${day.date}`}
                className="flex items-center justify-between py-1 border-b border-stone-50 last:border-0"
              >
                <div className="flex items-center gap-2">
                  <FileWarning size={14} className="text-amber-500" />
                  <div>
                    <p className="text-sm font-medium text-ink">{day.employee_name}</p>
                    <p className="text-xs text-stone-500">
                      {day.site_name} · {new Date(day.date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                <div className="text-xs text-stone-600">
                  {day.issue === 'missing_out' ? (
                    <>
                      Entrée <span className="font-medium num">{day.in_time}</span>
                      <span className="text-amber-600 ml-2">— sortie manquante</span>
                    </>
                  ) : (
                    <>
                      Sortie <span className="font-medium num">{day.out_time}</span>
                      <span className="text-amber-600 ml-2">— entrée manquante</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Absences */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card
          title={`Absences non justifiées — ${unjustifiedAbsent.length}`}
          className={unjustifiedAbsent.length > 0 ? 'border-accent/30' : ''}
        >
          {presenceLoading ? (
            <p className="text-sm text-stone-400">Chargement...</p>
          ) : unjustifiedAbsent.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-stone-400">
              <AlertTriangle size={14} /> Aucune absence non justifiée
            </div>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {unjustifiedAbsent.map((emp) => (
                <div
                  key={emp.id}
                  className="flex items-center justify-between py-1 border-b border-stone-50 last:border-0"
                >
                  <div>
                    <p className="text-sm font-medium text-ink">{emp.name}</p>
                    <p className="text-xs text-stone-500">
                      {emp.site_name} · {emp.department}
                    </p>
                  </div>
                  <Badge variant="danger">
                    {emp.scheduled_start}–{emp.scheduled_end}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title={`Absences justifiées — ${justifiedAbsent.length}`}>
          {presenceLoading ? (
            <p className="text-sm text-stone-400">Chargement...</p>
          ) : justifiedAbsent.length === 0 ? (
            <p className="text-sm text-stone-400">Aucune absence déclarée</p>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {justifiedAbsent.map((emp) => (
                <div
                  key={emp.id}
                  className="flex items-center justify-between py-1 border-b border-stone-50 last:border-0"
                >
                  <div>
                    <p className="text-sm font-medium text-ink">{emp.name}</p>
                    <p className="text-xs text-stone-500">
                      {emp.site_name} · {emp.department}
                    </p>
                  </div>
                  <Badge variant="info">{emp.absence_reason}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
